import React, { useState, useMemo, useEffect } from "react";
import Modal from "../../components/Modal";
import "./designs.scss";
import ProductModal from "../../components/ProductModal";
import Helmet from "react-helmet";
import { useHistory } from "react-router-dom";
import LeftNavigation from "../../common/views/LeftNavigation";

import styles from "./DesignsContainer.module.scss";
import { useSelector } from "react-redux";
import DesignStateService from "../../service/DesignStateService";
import DesignService from "../../service/DesignService";
import { routeFormatter } from "../../utils/Formatter";
import { ROUTES } from "../../utils/Constant";
import { useDesignFetcher } from "../../utils/DesignsFetcherHooks";
import TopSection from "./TopSection";
import DashboardToolbar from "../../components/DashboardToolbar";
import CreatingCanvaModal from "../../components/CreatingCanvaModal";
import UtilityButton from "../../components/UtilityButton";
import plusIcon from "../../assets/editor/plus_icon.svg";
import ruleIcon from "../../assets/editor/rule.svg";
import { useTranslation } from "react-i18next";
import DesignWithErrorsCard from "../../components/Design/DesignWithErrorsCard";
import DesignCard from "../../components/Design/DesignCard";
import { toast } from "react-toastify";

const Designs = (props) => {
  const { t } = useTranslation();

  const history = useHistory();
  const { fetchMyDesignsAndUpdateStore } = useDesignFetcher();
  const projectsState = useSelector((state) => state.projects);

  const { match, showNewDesignModal = false } = props;

  //Load designs to get the latest
  useEffect(() => {
    fetchMyDesignsAndUpdateStore();
  }, [fetchMyDesignsAndUpdateStore, match.params.id]); //match.params.id is required so we fetch every time the user clicks on a project

  const projects = useMemo(() => {
    return projectsState?.projects;
  }, [projectsState]);

  const projectsLoaded = useMemo(() => {
    return projectsState?.loaded;
  }, [projectsState]);

  const designsReduxState = useSelector((state) => state.designs);
  const designs = useMemo(() => {
    return designsReduxState?.designs;
  }, [designsReduxState]);

  const userInfo = useSelector((state) => state.userInfo);

  const project = useMemo(() => {
    const projectId = match.params.id;
    return projects.find((p) => p.id === projectId);
  }, [projects, match.params.id]);

  const projectDesigns = useMemo(
    () =>
      designs
        .filter((d) => d.project_id === project?.id)
        .sort((a, b) => {
          if (a?.family?.name === b?.family?.name) {
            if (a?.product?.name === b?.product?.name) {
              if (a.latestUpdate < b.latestUpdate) {
                return 1;
              } else {
                return -1;
              }
            } else if (a?.product?.name < b?.product?.name) {
              return 1;
            } else {
              return -1;
            }
          } else if (a?.family?.name < b?.family?.name) {
            return 1;
          } else {
            return -1;
          }
        }),
    [designs, project]
  );

  const [productPopup, setProductPopup] = useState(showNewDesignModal);
  const [creating, setCreating] = useState(false);
  const addDesign = () => {
    setProductPopup(true);
  };

  const onCloseProductPopup = () => {
    setProductPopup(false);
  };

  const createDesign = async (family, product, newDesignName) => {
    setCreating(true);
    try {
      const res = await DesignStateService.createDesignState({
        family,
        product,
      });

      const {
        data: { designId },
      } = res;

      const projectId = match.params.id;

      //TODO test if success
      await DesignService.addDesign(
        {
          projectId,
          designId,
          name: newDesignName,
        },
        product
      );

      await fetchMyDesignsAndUpdateStore();

      history.push(routeFormatter(ROUTES.STUDIO, { params: { designId } }));
    } catch (error) {
      console.error(error);
      setCreating(false);
    }
  };

  //If project is not found, then we go home
  useEffect(() => {
    if (projectsLoaded && !project) {
      toast.warn(t("project.not_found"));
      history.push(ROUTES.HOME);
      return;
    }
  }, [projectsLoaded, project, history, t]);

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{`${t("pages.projects.title", { id: project?.name })}`}</title>
      </Helmet>
      <DashboardToolbar />
      <div className={styles.dashboardContainer}>
        <LeftNavigation />
        <div className={styles.centerContainer}>
          <TopSection
            project={project}
            canDeleteProject={userInfo.canDeleteProject}
            canRenameProject={userInfo.canRenameProject}
          />

          <div className={styles.utilityButtonContainer}>
            {userInfo.canAddDesign && (
              <UtilityButton
                text={t("design.new_design")}
                subtext={t("design.let_your_creativity_flow")}
                icon={plusIcon}
                onClick={addDesign}
              />
            )}
            <UtilityButton
              text={t("project.collect_sizes")}
              subtext={t("project.gather_your_team_size")}
              icon={ruleIcon}
              onClick={() =>
                history.push(
                  routeFormatter(ROUTES.SIZES, {
                    params: { projectId: project?.id },
                  })
                )
              }
            />
          </div>
          <div className={styles.designContainer}>
            {projectDesigns &&
              projectDesigns.map((design) =>
                //If design is in error, then we can only delete it
                design.errorKey ? (
                  <DesignWithErrorsCard
                    key={design.id}
                    design={design}
                    project={project}
                    canDeleteDesign={userInfo.canDeleteDesign}
                  />
                ) : (
                  <DesignCard
                    key={design.id}
                    design={design}
                    project={project}
                    canDeleteDesign={userInfo.canDeleteDesign}
                    canRenameDesign={userInfo.canRenameDesign}
                    canMoveDesign={userInfo.canMoveDesign}
                    canAddDesign={userInfo.canAddDesign}
                  />
                )
              )}
          </div>
        </div>
      </div>
      <Modal show={creating} key="creating">
        <CreatingCanvaModal />
      </Modal>
      <Modal
        show={productPopup && !creating}
        closeModal={onCloseProductPopup}
        key="create"
      >
        <ProductModal
          createDesign={createDesign}
          onCloseModal={onCloseProductPopup}
        />
      </Modal>
    </div>
  );
};

export default Designs;
